import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, MapPin, Briefcase, ExternalLink, Instagram, Globe, Mail, Phone, Check } from 'lucide-react';
import recruitmentApi from '../../utils/agencyRecruitmentApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Iter-228 — Public branded agency recruitment page.
 * Mounted at `/agency/:slug` (no auth required).
 *
 * Sections (in scroll order):
 *  1. Hero — banner + logo + name + tagline
 *  2. Why Join {Agency}
 *  3. About / description + recruiting message
 *  4. Hiring markets + roles
 *  5. Brands We've Worked With
 *  6. Agency Requirements / Onboarding checklist
 *  7. Contact info + socials
 *  8. Sticky bottom CTA → SmartEmailJoin
 */
const AgencyRecruitmentPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await recruitmentApi.publicGet(slug);
        if (cancelled) return;
        if (res.data?.redirect_slug) {
          navigate(`/agency/${res.data.redirect_slug}${window.location.search}`, { replace: true });
          return;
        }
        setPage(res.data);
        // Fire-and-forget enriched visit tracking with UTM
        recruitmentApi.trackVisit(slug, {
          utm_source: params.get('utm_source'),
          utm_medium: params.get('utm_medium'),
          utm_campaign: params.get('utm_campaign'),
          referrer: document.referrer || null,
        }).catch(() => {});
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate, params]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }
  if (notFound || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center">
        <div data-testid="agency-page-notfound">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Page not available</h1>
          <p className="text-slate-600 max-w-md">
            This recruitment page may not be published yet, or the link may have changed.
            <br />
            <Link to="/" className="text-cyan-700 underline">Return to iEvntür</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 pb-32" data-testid="agency-recruitment-page">
      {/* Hero */}
      <section className="relative w-full h-80 md:h-[28rem] overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950">
        {page.banner_url && (
          <img
            src={page.banner_url}
            alt={`${page.company_name} banner`}
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
        )}
        {/* gradient + grain overlay for premium feel */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/55 to-transparent" />
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }} />

        {/* Hiring badge — top right */}
        {(page.hiring_markets?.length > 0 || page.hiring_roles?.length > 0) && (
          <div className="absolute top-5 right-5 md:top-7 md:right-7">
            <span className="inline-flex items-center gap-1.5 bg-emerald-500/95 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm" data-testid="now-hiring-badge">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              Now hiring
            </span>
          </div>
        )}

        <div className="absolute bottom-7 left-6 right-6 md:bottom-12 md:left-12 md:right-12 flex items-end gap-4 md:gap-6">
          {page.logo_url ? (
            <img
              src={page.logo_url}
              alt={`${page.company_name} logo`}
              className="w-20 h-20 md:w-32 md:h-32 rounded-2xl object-cover bg-white border-4 border-white shadow-2xl ring-1 ring-black/10"
              data-testid="agency-logo"
            />
          ) : (
            <div className="w-20 h-20 md:w-32 md:h-32 rounded-2xl bg-white/10 border-4 border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-4xl font-bold shadow-2xl">
              {(page.company_name || '?').charAt(0)}
            </div>
          )}
          <div className="text-white flex-1 min-w-0 pb-1">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight tracking-tight" data-testid="agency-name">
              {page.company_name}
            </h1>
            {page.tagline && (
              <p className="text-base md:text-xl text-white/85 mt-2 leading-relaxed" data-testid="agency-tagline">
                {page.tagline}
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 md:px-8 -mt-8 relative z-10 space-y-8">

        {/* Why join */}
        {page.why_join_us && (
          <section className="bg-white rounded-2xl shadow-lg p-7 md:p-10 border border-slate-200" data-testid="why-join-section">
            <h2 className="text-xs uppercase font-bold tracking-[0.2em] text-cyan-700 mb-3">Why Join {page.company_name}</h2>
            <p className="text-lg md:text-2xl text-slate-800 leading-relaxed whitespace-pre-line font-medium">
              {page.why_join_us}
            </p>
          </section>
        )}

        {/* About */}
        {(page.description || page.recruiting_message) && (
          <section className="bg-white rounded-2xl shadow p-6 md:p-8 border border-slate-100" data-testid="about-section">
            {page.description && (
              <p className="text-base text-slate-700 leading-relaxed whitespace-pre-line">
                {page.description}
              </p>
            )}
            {page.recruiting_message && (
              <p className="mt-4 text-base text-slate-800 leading-relaxed whitespace-pre-line italic border-l-4 border-cyan-500 pl-4">
                {page.recruiting_message}
              </p>
            )}
          </section>
        )}

        {/* Markets + Roles */}
        {((page.hiring_markets?.length || 0) + (page.hiring_roles?.length || 0)) > 0 && (
          <section className="grid md:grid-cols-2 gap-4" data-testid="markets-roles-section">
            {page.hiring_markets?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
                <div className="flex items-center gap-2 text-cyan-700 text-xs uppercase tracking-widest font-bold mb-3">
                  <MapPin className="w-4 h-4" /> Hiring In
                </div>
                <div className="flex flex-wrap gap-2">
                  {page.hiring_markets.map((m, i) => (
                    <Badge key={i} className="bg-slate-100 text-slate-800 hover:bg-slate-200 text-sm" data-testid={`market-${i}`}>{m}</Badge>
                  ))}
                </div>
              </div>
            )}
            {page.hiring_roles?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
                <div className="flex items-center gap-2 text-cyan-700 text-xs uppercase tracking-widest font-bold mb-3">
                  <Briefcase className="w-4 h-4" /> Open Roles
                </div>
                <div className="flex flex-wrap gap-2">
                  {page.hiring_roles.map((r, i) => (
                    <Badge key={i} className="bg-cyan-50 text-cyan-800 border border-cyan-200 text-sm" data-testid={`role-${i}`}>{r}</Badge>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Brands We've Worked With */}
        {page.brands_worked_with?.length > 0 && (
          <section className="bg-white rounded-2xl shadow-md p-6 md:p-10 border border-slate-100" data-testid="brands-section">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-xs uppercase font-bold tracking-[0.2em] text-cyan-700">Trusted by</h2>
              <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1.5">Brands we&apos;ve worked with</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6 items-center">
              {page.brands_worked_with.map((b, i) => (
                <div key={i}
                     className="group bg-slate-50/50 rounded-xl p-3 h-24 flex flex-col items-center justify-center border border-slate-100 hover:border-slate-300 hover:shadow-sm transition"
                     data-testid={`brand-${i}`}>
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.name || ''} className="max-h-12 max-w-full object-contain grayscale group-hover:grayscale-0 opacity-75 group-hover:opacity-100 transition" />
                  ) : (
                    <span className="text-sm font-semibold text-slate-700">{b.name || '—'}</span>
                  )}
                  {b.logo_url && b.name && (
                    <span className="text-[10px] text-slate-500 mt-1.5 truncate w-full text-center opacity-0 group-hover:opacity-100 transition">{b.name}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Requirements */}
        {page.agency_requirements?.length > 0 && (
          <section className="bg-white rounded-2xl shadow p-6 md:p-8 border border-slate-100" data-testid="requirements-section">
            <h2 className="text-xs uppercase font-bold tracking-widest text-cyan-700 mb-4">Onboarding Requirements</h2>
            <ul className="space-y-3">
              {page.agency_requirements.map((req, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`requirement-${i}`}>
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <div className="font-medium text-slate-900">{req.label}</div>
                    {req.detail && <div className="text-sm text-slate-600 mt-0.5">{req.detail}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Contact + socials */}
        <section className="bg-white rounded-2xl shadow p-6 md:p-8 border border-slate-100" data-testid="contact-section">
          <h2 className="text-xs uppercase font-bold tracking-widest text-cyan-700 mb-4">Contact</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {page.contact_info?.email && (
              <a href={`mailto:${page.contact_info.email}`} className="flex items-center gap-2 text-slate-700 hover:text-cyan-700">
                <Mail className="w-4 h-4" /> {page.contact_info.email}
              </a>
            )}
            {(page.contact_info?.phone || page.agency_phone) && (
              <a href={`tel:${page.contact_info?.phone || page.agency_phone}`} className="flex items-center gap-2 text-slate-700 hover:text-cyan-700">
                <Phone className="w-4 h-4" /> {page.contact_info?.phone || page.agency_phone}
              </a>
            )}
            {page.website_url && (
              <a href={page.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-cyan-700">
                <Globe className="w-4 h-4" /> {page.website_url}
              </a>
            )}
            {page.instagram_handle && (
              <a href={`https://instagram.com/${page.instagram_handle.replace('@', '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-cyan-700">
                <Instagram className="w-4 h-4" /> @{page.instagram_handle.replace('@', '')}
              </a>
            )}
          </div>
        </section>

        <div className="text-center pt-4">
          <p className="text-xs text-slate-500">
            Powered by <Link to="/" className="text-cyan-700 hover:underline font-medium">iEvntür</Link> · One iEvntür profile · Belong to multiple agency teams · Your data stays yours.
          </p>
        </div>
      </div>

      {/* Sticky CTA */}
      <SmartEmailJoin slug={slug} page={page} />
    </div>
  );
};


/**
 * Iter-228 — Smart email-first signup. PopBookings behavior:
 *   1. Talent types email → check exists.
 *   2. If exists → password prompt → POST /join (links existing account).
 *   3. If new → full signup form → POST /apply (atomic create + roster).
 * Renders as a sticky bottom bar that expands when activated.
 */
const SmartEmailJoin = ({ slug, page }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('email'); // email | existing | new
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  // New-user fields
  const [form, setForm] = useState({ phone: '', password: '', first_name: '', last_name: '', city: '', state: '' });
  // Existing-user
  const [password, setPassword] = useState('');

  const onContinue = async () => {
    if (!email.trim()) return toast.error('Please enter your email');
    setBusy(true);
    try {
      const res = await recruitmentApi.checkEmail(slug, email.trim());
      if (res.data?.exists) {
        if (res.data.role !== 'talent') {
          toast.error('Only talent accounts can join an agency roster.');
          return;
        }
        setStep('existing');
      } else {
        setStep('new');
      }
    } catch (e) {
      toast.error('Could not check email — try again.');
    } finally {
      setBusy(false);
    }
  };

  const onExistingJoin = async () => {
    if (!password) return toast.error('Please enter your password');
    setBusy(true);
    try {
      const loginRes = await api.post('/auth/login', { email: email.trim(), password });
      // Set token so the `join` call below is authenticated.
      const token = loginRes.data?.token;
      if (!token) throw new Error('login failed');
      localStorage.setItem('token', token);
      const join = await recruitmentApi.existingUserJoin(slug);
      const status = join.data?.status;
      if (status === 'pending_approval') {
        toast.success(`Application sent — ${page.company_name} will review.`);
      } else {
        toast.success(`Welcome to ${page.company_name}!`);
      }
      navigate('/talent/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not sign in / join.');
    } finally {
      setBusy(false);
    }
  };

  const onNewApply = async () => {
    for (const f of ['phone', 'password', 'first_name', 'last_name', 'city', 'state']) {
      if (!form[f] || !String(form[f]).trim()) {
        return toast.error(`Please fill in ${f.replace('_', ' ')}`);
      }
    }
    setBusy(true);
    try {
      const res = await recruitmentApi.apply(slug, { email: email.trim(), ...form });
      const token = res.data?.token;
      if (token) localStorage.setItem('token', token);
      const status = res.data?.agency?.status;
      if (status === 'pending_approval') {
        toast.success(`Account created — ${page.company_name} is reviewing your application.`);
      } else {
        toast.success(`Welcome to ${page.company_name}!`);
      }
      navigate('/talent/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create account.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow-2xl p-4 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <div className="font-semibold text-slate-900">Join {page.company_name}&apos;s team</div>
            <div className="text-xs text-slate-500">One iEvntür account · work with multiple agencies</div>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-cyan-600 hover:bg-cyan-700 text-white text-base px-6 py-6 rounded-xl shadow-lg flex-1 sm:flex-none"
            data-testid="open-join-cta"
          >
            Apply to Join Our Team
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-0 md:p-6" onClick={() => setOpen(false)}>
      <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-slate-900 mb-1">Apply to join {page.company_name}</h3>
        <p className="text-sm text-slate-600 mb-5">
          Start by entering your email. If you already have an iEvntür account, we&apos;ll link it to <strong>{page.company_name}</strong>. <span className="text-slate-900 font-medium">One profile · no duplicates · join multiple teams.</span>
        </p>

        {step === 'email' && (
          <>
            <Label htmlFor="join-email">Email</Label>
            <Input id="join-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" className="mt-1" data-testid="join-email-input" />
            <Button onClick={onContinue} disabled={busy} className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700" data-testid="join-email-continue">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
            </Button>
          </>
        )}

        {step === 'existing' && (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm text-emerald-800" data-testid="join-existing-banner">
              We found your iEvntür account. Sign in to link <strong>{page.company_name}</strong> — no new profile needed.
            </div>
            <Label htmlFor="join-pw">Password</Label>
            <Input id="join-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="join-existing-password" />
            <Button onClick={onExistingJoin} disabled={busy} className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700" data-testid="join-existing-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Sign in & join ${page.company_name}'s team`}
            </Button>
            <button onClick={() => { setStep('email'); setPassword(''); }} className="text-xs text-slate-500 mt-3 hover:underline w-full">
              Use a different email
            </button>
          </>
        )}

        {step === 'new' && (
          <>
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 mb-4 text-sm text-cyan-800">
              Welcome — let&apos;s create your iEvntür account and join <strong>{page.company_name}</strong> in one step.
            </div>
            <div className="space-y-3">
              {[
                ['first_name', 'First name'], ['last_name', 'Last name'],
                ['phone', 'Phone'], ['city', 'City'], ['state', 'State'],
                ['password', 'Password'],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label htmlFor={`new-${k}`}>{label}</Label>
                  <Input
                    id={`new-${k}`}
                    type={k === 'password' ? 'password' : 'text'}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`join-new-${k}`}
                  />
                </div>
              ))}
            </div>
            <Button onClick={onNewApply} disabled={busy} className="w-full mt-5 bg-cyan-600 hover:bg-cyan-700" data-testid="join-new-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Create account & join ${page.company_name}'s team`}
            </Button>
            <button onClick={() => setStep('email')} className="text-xs text-slate-500 mt-3 hover:underline w-full">
              Use a different email
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AgencyRecruitmentPage;
